import { useState, useEffect, useRef } from "react";
import { getWorkerTemplateConfigForCountry } from "@/config/workerCountryTemplateMap";
import { getServerTemplateConfig, type ServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { isBaseTemplateFamily } from "@/lib/workerTemplateNames";
import {
  buildCountryTemplateContext,
  resolveEffectiveTemplateForVisitor,
  type CountryTemplateEntry,
} from "@/lib/heroCtaCompatibleTemplates";

export const WISHLIST_CONFIG_UPDATED_LS_KEY = "wishlist_config_updated";
export const WISHLIST_CONFIG_UPDATED_EVENT = "wishlist-config-updated";
// Mismo patrón para country-template-config: admin que guarda en
// /admin/country-template dispara estos eventos para que tabs activos del
// admin (preview en otro tab) revaliden inmediato sin esperar el poll 3s.
export const COUNTRY_TEMPLATE_UPDATED_LS_KEY = "country_template_updated";
export const COUNTRY_TEMPLATE_UPDATED_EVENT = "country-template-updated";

/** Coherente con Index.tsx / SEO (US, España, resto → LATAM incl. UNKNOWN). */
export type WorkerSeoRegion = "US" | "SPAIN" | "LATAM";

function seoRegionFromCountry(country: string | undefined | null): WorkerSeoRegion {
  if (country === "US") return "US";
  if (country === "ES") return "SPAIN";
  return "LATAM";
}

type CtaType = "none" | "birthday" | "wishlist" | "community" | "dual" | "whatsapp" | "instagram";
type GeoScope = "city_ar" | "province_ar" | "country";

interface WishlistGeoEntry {
  scope: GeoScope;
  geo_code: string;
  cta_type: CtaType;
  label?: string | null;
  url?: string | null;
  emoji?: string | null;
  enabled?: boolean;
}

type CtaFlags = ReturnType<typeof flagsFromCtaType>;

function normalizeGeoKey(value: string | null | undefined): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

// Brand decision: instagram cta_type deshabilitado globalmente. Mirror exacto
// del WORKER_INSTAGRAM_CTA_ENABLED flag en worker.js. Sin esta paridad, el
// worker emite hideCta=true (porque normaliza instagram→none) y el frontend
// resolver sigue retornando showInstagram=true → flagsChanged trigger →
// reload loop infinito. Setear a true acá Y en el worker si reactivan.
const FRONTEND_INSTAGRAM_CTA_ENABLED = false;

function flagsFromCtaType(ctaType: CtaType | null) {
  // Paridad worker normalizeWorkerCtaType: instagram desactivado → none.
  const effective: CtaType | null =
    ctaType === "instagram" && !FRONTEND_INSTAGRAM_CTA_ENABLED ? "none" : ctaType;
  return {
    hideCta: effective === "none" || effective == null,
    showWishlist: effective === "wishlist",
    showBirthday: effective === "birthday",
    showCommunity: effective === "community",
    showDual: effective === "dual",
    showWhatsappCommunity: effective === "whatsapp",
    showInstagram: effective === "instagram",
  };
}

// Resolución: prioridad CITY_AR > PROVINCE_AR > country específico > country DEFAULT.
// CRÍTICO: el orden DEBE matchear worker.js (resolveCtaContent flow, líneas
// 3331-3344) — city_ar SIEMPRE gana sobre province_ar para el mismo visitor.
// Sin esta paridad, cuando admin pone city_ar=Yerba Buena→"wishlist" y
// province_ar=Tucumán→"community", el worker emite wishlist pero el frontend
// resolvía community → flags difieren → reload loop.
// Bug histórico (commit anterior tenía province primero, opposite del worker).
function resolveEntryForVisitor(
  entries: WishlistGeoEntry[],
  country: string,
  province: string | null | undefined,
  city: string | null | undefined,
): WishlistGeoEntry | null {
  const active = entries.filter((e) => e.enabled !== false);
  const countryCode = country.toUpperCase();
  const cityNorm = normalizeGeoKey(city);
  const provinceNorm = normalizeGeoKey(province);

  // 1. city_ar override (más específico — ej. "Yerba Buena" en Tucumán)
  if (countryCode === "AR" && cityNorm) {
    const cityEntry = active.find(
      (e) => e.scope === "city_ar" && normalizeGeoKey(e.geo_code) === cityNorm,
    );
    if (cityEntry) return cityEntry;
  }

  // 2. province_ar override (cubre todas las ciudades de la provincia sin city_ar)
  if (countryCode === "AR" && provinceNorm) {
    const provinceEntry = active.find(
      (e) => e.scope === "province_ar" && normalizeGeoKey(e.geo_code) === provinceNorm,
    );
    if (provinceEntry) return provinceEntry;
  }

  // 3. country específico (ej. "AR" para todos los argentinos sin override geo)
  const countryEntry = active.find(
    (e) => e.scope === "country" && e.geo_code.toUpperCase() === countryCode,
  );
  if (countryEntry) return countryEntry;

  // 4. country=DEFAULT (fallback intl)
  const defaultEntry = active.find(
    (e) => e.scope === "country" && e.geo_code.toUpperCase() === "DEFAULT",
  );
  return defaultEntry ?? null;
}

function resolveCtaTypeFromEntries(
  entries: WishlistGeoEntry[],
  country: string,
  province: string | null | undefined,
  city: string | null | undefined,
): CtaType | null {
  const entry = resolveEntryForVisitor(entries, country, province, city);
  return entry ? entry.cta_type : null;
}

function ctaFlagsChanged(prev: ServerTemplateConfig, next: CtaFlags): boolean {
  return (
    !!prev.hideCta !== next.hideCta ||
    !!prev.showWishlist !== next.showWishlist ||
    !!prev.showBirthday !== next.showBirthday ||
    !!prev.showCommunity !== next.showCommunity ||
    !!prev.showDual !== next.showDual ||
    !!prev.showWhatsappCommunity !== next.showWhatsappCommunity ||
    !!prev.showInstagram !== next.showInstagram
  );
}

// Compara contenido del hero CTA (label/url/emoji) entre lo inyectado por el
// Worker al cargar la página y lo que las entries actuales resolverían ahora.
// Usado para detectar cambios "silenciosos" donde admin solo edita label/url/
// emoji manteniendo el mismo cta_type — el detector de flags no los nota.
//
// CRÍTICO — evitar loop infinito de reload:
//   Si admin NO setea custom label/url/emoji (caso default), entry.label es
//   null/"" mientras prev.ctaLabel es el default resuelto por el worker (ej.
//   "Mejores Amigos" para cta_type=instagram). La comparación naïve
//   prev !== next devuelve TRUE → reload → mount → fetch entries → diff → ...
//   El cooldown 15s lo limita pero igual recarga cada 15s, mata UX.
//
// Fix: solo consideramos cambio si entry tiene contenido custom NO vacío.
//   - Admin sin custom (entry vacío) → confiamos en worker defaults, no reload
//   - Admin con custom (entry no vacío) → si difiere del injected, reload
// Edge case: admin "borra" un custom previo → entry queda vacío y NO detectamos.
// Próximo refresh natural del visitor (poll de site-settings o cierre+abrir)
// recoge el default. Decisión aceptable: evitar el loop tiene prioridad.
/** Detecta el side-channel de instagram tier (label "instagram_price_tier:*",
 *  url checkout MP del preapproval_plan_id). Esos campos NO son user-facing,
 *  los emite el worker como defaults igual — comparar contra ellos generaba
 *  loops infinitos cuando admin no había limpiado el leftover después de
 *  cambiar cta_type de instagram a otro tipo. Skip total del check. */
function isInstagramTierLeftover(entry: WishlistGeoEntry | null): boolean {
  if (!entry) return false;
  const label = (entry.label ?? "").trim();
  const url   = (entry.url   ?? "").trim();
  if (/^instagram_price_tier:/i.test(label)) return true;
  if (/mercadopago\.com\.ar\/subscriptions\/checkout\?preapproval_plan_id=/i.test(url)) return true;
  return false;
}

function ctaContentChanged(
  prev: ServerTemplateConfig,
  entry: WishlistGeoEntry | null,
): boolean {
  // Side-channel tier leftover → worker emite defaults, frontend ni compara.
  if (isInstagramTierLeftover(entry)) return false;

  const nextLabel = (entry?.label ?? "").trim();
  const nextUrl   = (entry?.url   ?? "").trim();
  const nextEmoji = (entry?.emoji ?? "").trim();
  const hasCustomContent = nextLabel !== "" || nextUrl !== "" || nextEmoji !== "";
  if (!hasCustomContent) return false;

  const prevLabel = (prev.ctaLabel ?? "").trim();
  const prevUrl   = (prev.ctaUrl   ?? "").trim();
  const prevEmoji = (prev.ctaEmoji ?? "").trim();
  return nextLabel !== prevLabel || nextUrl !== prevUrl || nextEmoji !== prevEmoji;
}

/**
 * Hook to determine which template to use based on server configuration
 * All configuration comes from Cloudflare Worker injection
 */
export const useTemplateSelection = () => {
  // 15s cooldown (antes 60s): el write-through admin/wishlist-config corre en
  // paralelo (worker_refresh + cf_api), así que la KV global está fresca en
  // ~1-2s. Un cooldown largo era seguro cuando dependíamos del TTL natural
  // (60s) — ya no, y solo retrasa el "real-time" feel sin proteger nada.
  const CTA_REFRESH_COOLDOWN_MS = 15_000;
  const CTA_REFRESH_SESSION_KEY = "cta_refresh_last_reload_at";
  // Initialize with server config
  const [config, setConfig] = useState<ServerTemplateConfig>(() => {
    const serverConfig = getServerTemplateConfig();
    
    if (serverConfig) {
      return serverConfig;
    }
    
    // Fallback if no server config (shouldn't happen in production)
    return {
      country: 'UNKNOWN',
      templateName: 'BaseTemplate',
      bioLayout: null,
      language: 'en-US',
      province: null,
      cfCountry: 'UNKNOWN',
      timezone: '',
      city: '',
      detectionMethod: 'fallback'
    };
  });
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  
  // Admin geo preview — dos paths de auth que el worker honra:
  //   1. `?debug=SECRET&country=US&…` (legacy admin previews)
  //   2. `?geo_sim=1&gst=TOKEN&country=DE&…` (super admin / QA toolbar real)
  //
  // Antes este effect strippeaba `country` y reloadeaba cuando NO había
  // `?debug=`. El toolbar usa geo_sim (sin debug), así que el strip + reload
  // mataba el override: 1er paint correcto → reload sin country → worker
  // volvía a la geo real (AR) y el banner mostraba "Worker emitió BaseTemplate".
  //
  // Si hay debug O geo_sim, NO tocamos la URL: __TEMPLATE_CONFIG__ ya es la
  // fuente de verdad y useLiveCTA reenvía los params al poll del snapshot.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const countryOverride = params.get("country");
    const debugKey = params.get("debug");
    const hasDebugSession = !!(debugKey && debugKey.trim());
    const hasGeoSimSession = params.get("geo_sim") === "1";

    if (!countryOverride || countryOverride.length !== 2) return;

    if (hasDebugSession || hasGeoSimSession) {
      // Worker-authorized preview: leave querystring intact so hard reload /
      // snapshot poll keep the simulated country.
      return;
    }

    // Path legacy sin debug ni geo_sim — no funciona end-to-end con el worker
    // pero lo mantenemos por backwards-compat con tools externos.
    const overrideConfig = getWorkerTemplateConfigForCountry(countryOverride);
    setConfig(overrideConfig);
    params.delete("country");
    const newUrl = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", newUrl);
    window.location.reload();
  }, []);

  // Live sync for WishlistAdmin CTA updates:
  // re-resolve CTA flags from /api/wishlist-config and merge into the current
  // worker config so BaseTemplate reflects changes quickly without waiting for a full reload.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const canHardRefreshNow = () => {
      try {
        const last = Number(sessionStorage.getItem(CTA_REFRESH_SESSION_KEY) || "0");
        return !Number.isFinite(last) || Date.now() - last > CTA_REFRESH_COOLDOWN_MS;
      } catch {
        return true;
      }
    };

    const markHardRefresh = () => {
      try {
        sessionStorage.setItem(CTA_REFRESH_SESSION_KEY, String(Date.now()));
      } catch {
        // ignore storage write failures
      }
    };

    // refreshFromWishlistConfig: detects whether the admin has touched the
    // CTA config since this tab booted and triggers a single page reload so
    // the Cloudflare Worker re-injects __TEMPLATE_CONFIG__ from scratch.
    //
    // We deliberately do NOT setConfig({ ...nextFlags }) here anymore: the
    // local resolver had simpler precedence than the Worker (e.g. it didn't
    // implement provinceNoneLocksMappedCity), so applying its output would
    // create a tiny window where the visitor saw flags that diverged from
    // what the admin sees in the Worker → __TEMPLATE_CONFIG__ pipeline.
    // The brand rule is "no diferencial entre admin y front": the Worker is
    // the only resolver, full stop. We only use the local resolver as a
    // *change detector* here.
    const refreshFromWishlistConfig = async (_opts?: { hardRefreshOnChange?: boolean }) => {
      // `_opts` kept for back-compat with existing event listeners.
      // hardRefreshOnChange is now implicit — every detected change triggers
      // a reload as long as we're not on the admin editor and the cooldown
      // window has elapsed (avoids reload loops).
      const base = getServerTemplateConfig();
      if (!base) return;
      // Gate canónica: el wishlist poll → reload SOLO aplica en BaseTemplate.
      // Otros templates (VideoTemplate, DualCTAVideoLanding, BioLink*) tienen
      // su propio polling (useLiveCTA → snapshot endpoint cada 3s) que no
      // require reload. Sin este gate, un visitor en VideoTemplate podría
      // recibir reloads innecesarios cuando admin cambia hero-cta de otra geo.
      if (base.templateName !== "BaseTemplate") return;
      try {
        const res = await fetch(`/api/wishlist-config?_t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { entries?: WishlistGeoEntry[] };
        if (!Array.isArray(data.entries) || cancelled) return;

        // Resolver entry completa (cta_type + label/url/emoji) y compararla
        // contra lo que el Worker inyectó al cargar la página. Dos vías de
        // cambio:
        //   1. cta_type → flags difieren → template/CTA cambia → reload.
        //   2. label/url/emoji custom difieren → mismo cta_type pero texto/
        //      destino/icono cambió desde admin → reload para que el Worker
        //      re-inyecte el contenido resuelto.
        const entry = resolveEntryForVisitor(
          data.entries,
          base.country,
          base.province,
          base.city,
        );
        const nextFlags = flagsFromCtaType(entry ? entry.cta_type : null);
        const flagsChangedNow = ctaFlagsChanged(configRef.current, nextFlags);
        const contentChangedNow = ctaContentChanged(configRef.current, entry);
        if (!flagsChangedNow && !contentChangedNow) return;

        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/admin/hero-cta" &&
          canHardRefreshNow()
        ) {
          markHardRefresh();
          window.location.reload();
        }
      } catch {
        // best-effort sync; keep current config on network failures
      }
    };

    // Polling paralelo de country-template-config. Si el admin cambia el
    // template asignado para el país del visitor (ej. AR de BaseTemplate a
    // DualCTAVideoLanding), el SPA visitor NO lo veía hasta hard reload
    // manual — el __TEMPLATE_CONFIG__ inyectado en el SSR quedaba pegado en
    // el browser. Polling detecta el cambio y dispara reload con mismo
    // cooldown (15s) que el wishlist para evitar loops.
    const refreshFromCountryTemplate = async () => {
      const base = getServerTemplateConfig();
      if (!base || !base.country || base.country === "UNKNOWN") return;
      try {
        const res = await fetch(`/api/country-template-config?_t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          entries?: Array<Partial<CountryTemplateEntry>>;
        };
        if (!Array.isArray(data.entries) || cancelled) return;
        // Misma cascada que worker.js: city > state_us > country > DEFAULT > map.
        // CRÍTICO pasar la city del worker: sin ella, un override por ciudad
        // haría que este poll recompute el template por country/state, difiera
        // del templateName inyectado y dispare window.location.reload() en
        // loop cada 15s.
        // Safe cast: we already validated it's an array; build fn skips incomplete entries.
        const { countryOverrides, stateOverrides, cityOverrides } =
          buildCountryTemplateContext(data.entries as CountryTemplateEntry[]);
        const rawCfg = (
          window as unknown as {
            __TEMPLATE_CONFIG__?: {
              usState?: string;
              city?: string;
              /** Canonical after city_catalog alias — prefer for poll match. */
              cityKey?: string;
              templateSource?: string;
            };
          }
        ).__TEMPLATE_CONFIG__;
        // privacy_lock: el worker forzó BaseTemplate por VPN/proxy/datacenter.
        // Este poll no puede recomputar ese veredicto (es server-side, por IP)
        // — recomputar por geo daría el template geo-dirigido y dispararía
        // reload loop. El template queda como el worker lo sirvió.
        if (rawCfg?.templateSource === "privacy_lock") return;
        // Prefer cityKey (canonical after worker city_catalog alias) over raw
        // CF city — otherwise "New York City" misses override key "new york"
        // and the poll hard-reloads every ~15s.
        const expectedTemplate = resolveEffectiveTemplateForVisitor(
          base.country,
          countryOverrides,
          stateOverrides,
          rawCfg?.usState ?? null,
          {
            city:
              (typeof rawCfg?.cityKey === "string" && rawCfg.cityKey.trim()
                ? rawCfg.cityKey
                : null) ??
              rawCfg?.city ??
              base.city ??
              null,
            cityOverrides,
          },
        );
        if (expectedTemplate === configRef.current.templateName) return;
        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/admin/country-template" &&
          canHardRefreshNow()
        ) {
          markHardRefresh();
          window.location.reload();
        }
      } catch {
        // best-effort
      }
    };

    void refreshFromWishlistConfig();
    void refreshFromCountryTemplate();
    // 3s polling (antes 10s). Cada check son 2 GETs ~1KB a endpoints KV-cached
    // en el Worker (<10ms cada uno). Costo CPU/network despreciable. Cuando
    // el tab está oculto, el browser throttle setInterval (>=1s), así que
    // tabs en background no abusan.
    timer = setInterval(() => {
      void refreshFromWishlistConfig();
      void refreshFromCountryTemplate();
    }, 3_000);

    const onStorage = (e: StorageEvent) => {
      if (e.key === WISHLIST_CONFIG_UPDATED_LS_KEY) {
        void refreshFromWishlistConfig({ hardRefreshOnChange: true });
      }
      // Country template también dispara revalidación inmediata cross-tab
      // — admin guarda template para país X, tabs SPA en país X reload sin
      // esperar el poll 3s.
      if (e.key === COUNTRY_TEMPLATE_UPDATED_LS_KEY) {
        void refreshFromCountryTemplate();
      }
    };
    const onLocalUpdate = () => {
      void refreshFromWishlistConfig({ hardRefreshOnChange: true });
    };
    const onLocalCountryTemplateUpdate = () => {
      void refreshFromCountryTemplate();
    };
    // Cuando el usuario vuelve al tab tras estar minimizado/oculto, revalidamos
    // inmediatamente — el setInterval pudo haber sido throttled y la admin pudo
    // haber tocado el CTA / template entre medias.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshFromWishlistConfig();
        void refreshFromCountryTemplate();
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(WISHLIST_CONFIG_UPDATED_EVENT, onLocalUpdate);
    window.addEventListener(COUNTRY_TEMPLATE_UPDATED_EVENT, onLocalCountryTemplateUpdate);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(WISHLIST_CONFIG_UPDATED_EVENT, onLocalUpdate);
      window.removeEventListener(
        COUNTRY_TEMPLATE_UPDATED_EVENT,
        onLocalCountryTemplateUpdate,
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
  
  const shouldUseBaseTemplate = () => isBaseTemplateFamily(config.templateName);
  const shouldUseBioLinkTemplate = () => config.templateName === "BioLinkTemplate";
  const shouldUseArgentinaBioTemplate = () => config.templateName === "ArgentinaBioTemplate";
  const shouldUseBioTemplatePremium = () => config.templateName === "BioTemplatePremium";

  const region = seoRegionFromCountry(config.country);

  return {
    // Core template data
    templateName: config.templateName,
    shouldUseBaseTemplate,
    shouldUseBioLinkTemplate,
    shouldUseArgentinaBioTemplate,
    shouldUseBioTemplatePremium,
    
    // Location data
    country: config.country,
    region,
    province: config.province,
    language: config.language,
    
    // Template variants
    isSpainBio: config.templateName === "BioLinkTemplate" && config.bioLayout === "spain_bio_link",
    isUSBio: config.templateName === "BioLinkTemplate" && config.bioLayout === "us_bio_link",
    isBaseTemplate: isBaseTemplateFamily(config.templateName),
    isArgentinaBio: config.templateName === "ArgentinaBioTemplate",
    isPremiumInvite: config.templateName === "BioTemplatePremium",
    
    // Location flags
    isArgentina: config.country === "AR",
    isBuenosAires: config.province === "Buenos Aires" || config.province === "CABA",

    // Best Friend Instagram (Close Friends) button (Argentina provinces: CABA, Buenos Aires, Santa Fe, etc.)
    bestfriendInstagram: !!config.whatsappBroadcast,
    hideCta: !!config.hideCta,
    showCommunity: !!config.showCommunity,
    showBirthday: !!config.showBirthday,
    showWishlist: !!config.showWishlist,
    showDual: !!config.showDual,
    // VPN/datacenter signal (tracking only — never blocks access)
    vpnDetected: config.vpnDetected === true,
    asOrganization: config.asOrganization || null,
    isSpainBioCountry: config.bioLayout === "spain_bio_link",
    
    // Bio layout info
    bioLayout: config.bioLayout,
    
    // Technical info
    timezone: config.timezone || (typeof window !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null),
    browserLang: typeof window !== "undefined" ? navigator.language : null,
    
    // Status - always ready with server config
    isEnhancedLoaded: true,
    detectionMethod: config.detectionMethod,
    isLoading: false,
    loadingStage: 'complete' as const,
    loadingText: '',
    isReady: true,
    
    // Loading protection - not needed
    isProtected: false,
    isInCooldown: false,
    canChangeTemplate: true,
    requestTemplateChange: () => {},
    loadingProgress: 100
  };
};
